import { useId, useMemo, useState } from 'react';

import type { Prospect } from '../../types';
import { Sheet } from '../components/Sheet';
import { dayLong, dayOf, isoPlusDays, quickDates, readTime } from '../lib/rdv';

interface AppointmentSheetProps {
  prospect: Prospect;
  /** La date part au serveur, l'heure reste sur l'appareil. Voir `lib/rdv.ts`. */
  onConfirm: (isoDay: string, time: string) => void;
  onClose: () => void;
  isSaving: boolean;
  now: Date;
}

/**
 * Fixer — ou déplacer — un rendez-vous.
 *
 * Trois raccourcis avant le calendrier, parce que c'est ce qui se dit au
 * téléphone : « demain », « jeudi », « la semaine prochaine ». Le calendrier
 * complet reste juste en dessous pour tout le reste, jamais caché derrière un
 * second geste.
 *
 * L'heure est facultative et le reste : un rendez-vous sans heure est un
 * rendez-vous valide, la date est ce qui compte pour la relance.
 */
export function AppointmentSheet(props: AppointmentSheetProps) {
  const { prospect, now } = props;
  const dateId = useId();
  const timeId = useId();

  const already = dayOf(prospect.nextFollowUpAt);
  const isMove = prospect.status === 'APPOINTMENT_SET' && already !== null;

  const [day, setDay] = useState(already ?? isoPlusDays(1, now));
  const [time, setTime] = useState(readTime(prospect.id) ?? '10:00');

  const shortcuts = useMemo(() => quickDates(now), [now]);

  return (
    <Sheet onClose={props.onClose} label={isMove ? 'Déplacer le rendez-vous' : 'Fixer un rendez-vous'}>
      <h2 className="sheet-title">
        {isMove ? 'Déplacer le rendez-vous' : 'Fixer un rendez-vous'}
      </h2>
      <p className="sheet-meta">{prospect.name}</p>

      <div className="chips-scroll" style={{ margin: '16px 0 14px' }}>
        {shortcuts.map((shortcut) => (
          <button
            key={shortcut.iso}
            type="button"
            className="chip"
            aria-pressed={day === shortcut.iso}
            onClick={() => setDay(shortcut.iso)}
          >
            {shortcut.label}
          </button>
        ))}
      </div>

      <div className="field-pair">
        <div className="field" style={{ flex: 1.4 }}>
          <label className="label" htmlFor={dateId}>
            Date
          </label>
          <input
            id={dateId}
            className="input"
            type="date"
            value={day}
            onChange={(event) => setDay(event.target.value)}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor={timeId}>
            Heure <span className="hint">sur cet appareil</span>
          </label>
          <input
            id={timeId}
            className="input"
            type="time"
            value={time}
            onChange={(event) => setTime(event.target.value)}
          />
        </div>
      </div>

      <p className="cost" style={{ display: 'block', marginTop: 10 }}>
        {day === '' ? 'Choisissez une date.' : dayLong(day)}
        {' — '}
        l’heure n’est retenue que sur cet appareil, la date suit la fiche partout.
      </p>

      <button
        type="button"
        className="btn btn-go btn-block"
        style={{ marginTop: 16 }}
        disabled={day === '' || props.isSaving}
        onClick={() => props.onConfirm(day, time)}
      >
        {props.isSaving
          ? 'Enregistrement…'
          : isMove
            ? 'Déplacer le rendez-vous'
            : 'Confirmer le rendez-vous'}
      </button>

      <button
        type="button"
        className="btn btn-ghost btn-block"
        style={{ marginTop: 6 }}
        onClick={props.onClose}
      >
        Annuler
      </button>
    </Sheet>
  );
}
